// ===========================================
// File Upload Zone - Drag & Drop File/Screenshot Uploads
// ===========================================
// Allows users to upload files via drag-and-drop or click-to-browse
// Displays uploaded files in a grid with preview and actions

import React, { useState, useCallback, useRef } from 'react';
import { 
  Upload, 
  Image, 
  FileText, 
  X, 
  Download, 
  Trash2,
  Paperclip,
  Eye,
  File,
  AlertCircle
} from 'lucide-react';
import { projectAPI } from '../services/api';
import './FileUploadZone.scss';

const FileUploadZone = ({ projectId, files = [], onFilesUpdated }) => {
  // State for drag-over visual feedback
  const [isDragging, setIsDragging] = useState(false);
  // State for upload progress
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // State for file preview modal
  const [previewFile, setPreviewFile] = useState(null);
  // State for delete confirmation
  const [deletingFileId, setDeletingFileId] = useState(null);
  
  // Ref for hidden file input
  const fileInputRef = useRef(null);

  // Handle drag events for visual feedback
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the dropzone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Handle file drop
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      await uploadFiles(droppedFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Handle file selection via input
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      await uploadFiles(selectedFiles);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Upload files to backend
  const uploadFiles = async (filesToUpload) => {
    setUploading(true);
    setUploadError(null);

    try {
      // Upload each file sequentially
      for (const file of filesToUpload) {
        // Check file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          setUploadError(`File "${file.name}" exceeds 10MB limit`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        await projectAPI.uploadFile(projectId, formData);
      }
      
      // Refresh project to get updated files list
      onFilesUpdated();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Failed to upload file(s)');
    } finally {
      setUploading(false);
    }
  };

  // Delete a file
  const handleDeleteFile = async (fileId) => {
    setDeletingFileId(fileId);
    try {
      await projectAPI.deleteFile(projectId, fileId);
      onFilesUpdated();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Failed to delete file');
    } finally {
      setDeletingFileId(null);
    }
  };

  // Get file icon based on MIME type
  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) {
      return <Image size={24} />;
    }
    if (mimeType === 'application/pdf') {
      return <FileText size={24} />;
    }
    return <File size={24} />;
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle download - create link and trigger download
  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="file-upload-zone">
      {/* Drop Zone Area */}
      <div
        className={`file-upload-zone__dropzone ${isDragging ? 'file-upload-zone__dropzone--active' : ''} ${uploading ? 'file-upload-zone__dropzone--uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        {/* Hidden file input for click-to-browse */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="file-upload-zone__input"
          accept="image/*,.pdf,.doc,.docx,.txt,.md,.json,.csv"
        />
        
        <div className="file-upload-zone__content">
          <Upload size={32} className="file-upload-zone__icon" />
          <p className="file-upload-zone__text">
            {uploading ? 'Uploading...' : isDragging ? 'Drop files here' : 'Drag & drop files here'}
          </p>
          <span className="file-upload-zone__hint">
            or click to browse • Images, PDFs, documents up to 10MB
          </span>
        </div>
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="file-upload-zone__error">
          <AlertCircle size={14} />
          {uploadError}
          <button onClick={() => setUploadError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Files Grid */}
      {files.length > 0 ? (
        <div className="file-upload-zone__files">
          {files.map((file) => {
            // Get file ID (handle both _id and id for MongoDB compatibility)
            const fileId = file._id || file.id;
            return (
            <div key={fileId} className="file-card">
              {/* Thumbnail or Icon */}
              <div 
                className="file-card__preview"
                onClick={() => file.mimeType?.startsWith('image/') && setPreviewFile(file)}
              >
                {file.mimeType?.startsWith('image/') ? (
                  <img src={file.url} alt={file.name} />
                ) : (
                  <div className="file-card__icon">
                    {getFileIcon(file.mimeType)}
                  </div>
                )}
              </div>
              
              {/* File Info */}
              <div className="file-card__info">
                <span className="file-card__name" title={file.name}>
                  {file.name}
                </span>
                <span className="file-card__meta">
                  {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                </span>
              </div>
              
              {/* Actions */}
              <div className="file-card__actions">
                {file.mimeType?.startsWith('image/') && (
                  <button 
                    onClick={() => setPreviewFile(file)} 
                    title="Preview"
                    className="file-card__action"
                  >
                    <Eye size={14} />
                  </button>
                )}
                <button 
                  onClick={() => handleDownload(file)}
                  title="Download"
                  className="file-card__action"
                >
                  <Download size={14} />
                </button>
                <button 
                  onClick={() => handleDeleteFile(fileId)} 
                  title="Delete"
                  className="file-card__action file-card__action--delete"
                  disabled={deletingFileId === fileId}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
          })}
        </div>
      ) : (
        <div className="file-upload-zone__empty">
          <Paperclip size={32} />
          <p>No files uploaded yet</p>
          <span>Upload screenshots, documents, or any project assets</span>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewFile && (
        <div className="file-preview-modal" onClick={() => setPreviewFile(null)}>
          <div className="file-preview-modal__content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="file-preview-modal__close" 
              onClick={() => setPreviewFile(null)}
            >
              <X size={24} />
            </button>
            <img src={previewFile.url} alt={previewFile.name} />
            <div className="file-preview-modal__info">
              <p>{previewFile.name}</p>
              <span>{formatFileSize(previewFile.size)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;

